import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { Spacing, FontSize, BorderRadius } from '../theme/colors';
import { AlertsScreen } from './AlertsScreen';
import { EventsScreen } from './EventsScreen';
import { GalleryScreen } from './GalleryScreen';
import { ChatScreen } from './ChatScreen';
import { ComplaintsScreen } from './ComplaintsScreen';
import { WebPageShell } from '../components/common/WebPageShell';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { useInboxUnread } from '../hooks/useInboxUnread';
import { UnreadBadge, UNREAD_TAB, UNREAD_TAB_TEXT } from '../components/common/UnreadBadge';
import { tabLabelWithCount } from '../utils/inboxUnread';

type Tab = 'Chat' | 'Events' | 'Gallery' | 'Complaints' | 'Alerts';

export function CommunicationScreen() {
  const route = useRoute<any>();
  const [activeTab, setActiveTab] = useState<Tab>(route.params?.initialTab || 'Chat');
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  // New chat messages and events (hooks/useInboxUnread): a red count on the tab, a light
  // highlight until it is opened. Opening Events marks the events seen; opening a chat
  // marks it read on the server.
  const { chat, events, refresh, markEventsSeen } = useInboxUnread();
  const unreadFor = (tab: string) => (tab === 'Chat' ? chat : tab === 'Events' ? events : 0);
  const selectTab = (tab: Tab) => {
    setActiveTab(tab);
    if (tab === 'Events') markEventsSeen();
    else refresh();
  };
  useEffect(() => {
    if (activeTab === 'Events' && events > 0) markEventsSeen();
  }, [activeTab, events, markEventsSeen]);

  useEffect(() => {
    if (route.params?.initialTab) {
      selectTab(route.params.initialTab);
    }
  }, [route.params?.initialTab]);

  return (
    <WebPageShell title="Communication" contentStyle={{ maxWidth: 1000 }}>
      <View style={{ flexGrow: 1 }}>

        <View style={[styles.tabContainer, isMobile && styles.tabContainerMobile]}>
          {(['Chat', 'Events', 'Gallery', 'Complaints', 'Alerts'] as Tab[]).map((tab) => {
            const isActive = activeTab === tab;
            const unread = unreadFor(tab);
            const isUnread = unread > 0 && !isActive;
            return (
              <TouchableOpacity
                key={tab}
                style={[styles.tabButton, isMobile && styles.tabButtonMobile, isUnread && [styles.unreadTab, UNREAD_TAB], isActive && styles.activeTabButton]}
                onPress={() => selectTab(tab)}
                accessibilityRole="tab"
                accessibilityState={{ selected: isActive }}
                accessibilityLabel={tabLabelWithCount(tab, unread)}
                testID={`inbox-tab-${tab}`}>
                <Text style={[styles.tabText, isMobile && styles.tabTextMobile, isUnread && UNREAD_TAB_TEXT, isActive && styles.activeTabText]}>
                  {tab}
                </Text>
                {!isActive && <UnreadBadge count={unread} testID={`inbox-tab-${tab}-badge`} />}
              </TouchableOpacity>
            );
          })}
        </View>

        <View style={styles.content}>
          {activeTab === 'Chat' && <ChatScreen isNested />}
          {activeTab === 'Events' && <EventsScreen isNested />}
          {activeTab === 'Gallery' && <GalleryScreen isNested />}
          {activeTab === 'Complaints' && <ComplaintsScreen isNested />}
          {activeTab === 'Alerts' && <AlertsScreen isNested />}
        </View>
      </View>
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.textPrimary,
    paddingHorizontal: Spacing.md,
    paddingTop: Spacing.md,
    paddingBottom: Spacing.sm,
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: colors.border,
    borderRadius: BorderRadius.md,
    marginHorizontal: Spacing.md,
    marginBottom: Spacing.md,
    padding: 4,
  },
  tabContainerMobile: {
    flexWrap: 'wrap',
  },
  tabButton: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: 8,
    alignItems: 'center',
    borderRadius: BorderRadius.sm,
  },
  tabButtonMobile: {
    flexBasis: '33%',
    paddingVertical: 6,
  },
  unreadTab: {
    borderWidth: 1,
  },
  activeTabButton: {
    backgroundColor: colors.primary,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  tabText: {
    fontSize: FontSize.sm,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  tabTextMobile: {
    fontSize: 12,
  },
  activeTabText: {
    color: '#FFFFFF',
    fontWeight: '700',
  },
  content: {
    flexGrow: 1,
  },
});
